import React, { useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Maximize2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo, organizationSchema, breadcrumbSchema } from "../lib/useSeo";
import { getProject } from "../lib/projects";
import { SERVICE_CONTENT } from "../lib/serviceContent";
import { Lightbox } from "../components/ui/Lightbox";
import { BeforeAfter } from "../components/ui/BeforeAfter";
import { ProjectGallery } from "../components/project/ProjectGallery";
import { ProjectCta } from "../components/project/ProjectCta";

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const isDE = locale === "de";
  const project = getProject(slug);
  const pageUrl = `https://www.joshifusiontech.de/projects/${slug}`;

  // Build flat list of gallery images shown in the gallery section + lightbox
  const galleryImages = useMemo(() => {
    if (!project) return [];
    const a = project.assets;
    const captions = [
      isDE ? "Hero — Lösung im Überblick" : "Hero — solution overview",
      isDE ? "Detail 1 — Kerninterface" : "Detail 1 — core interface",
      isDE ? "Detail 2 — Workflow" : "Detail 2 — workflow",
      isDE ? "Detail 3 — Integrationen" : "Detail 3 — integrations",
      isDE ? "Ergebnis-Dashboard" : "Results dashboard",
    ];
    return [
      { src: a.hero, alt: project.title[locale], caption: captions[0] },
      { src: a.gallery[0], alt: project.title[locale], caption: captions[1] },
      { src: a.gallery[1], alt: project.title[locale], caption: captions[2] },
      { src: a.gallery[2], alt: project.title[locale], caption: captions[3] },
      { src: a.dashboard, alt: project.title[locale], caption: captions[4] },
    ];
  }, [project, locale, isDE]);

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () => setLightboxIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  const next = () => setLightboxIndex((i) => (i + 1) % galleryImages.length);

  useSeo({
    title: project ? `${project.title[locale]} | Joshi FusionTech Media` : "Projekt | Joshi FusionTech Media",
    description: project ? project.summary[locale] : undefined,
    canonical: pageUrl,
    locale,
    jsonLd: project
      ? [
          organizationSchema(),
          breadcrumbSchema([
            { name: isDE ? "Start" : "Home", url: "https://www.joshifusiontech.de/" },
            { name: "Projects", url: "https://www.joshifusiontech.de/portfolio" },
            { name: project.title[locale], url: pageUrl },
          ]),
        ]
      : null,
  });

  if (!project) return <Navigate to="/portfolio" replace />;

  return (
    <main data-testid={`project-page-${slug}`} className="pt-32 pb-24">
      {/* HERO — title + image */}
      <section className="px-6 md:px-12 lg:px-24 mb-12">
        <div className="max-w-6xl mx-auto">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs font-mono text-white/50 flex-wrap">
              <li><Link to="/" className="hover:text-brand-orange">{isDE ? "Start" : "Home"}</Link></li>
              <li className="text-white/30">/</li>
              <li><Link to="/portfolio" className="hover:text-brand-orange">{isDE ? "Projekte" : "Projects"}</Link></li>
              <li className="text-white/30">/</li>
              <li className="text-white/80 truncate max-w-[60vw]">{project.title[locale]}</li>
            </ol>
          </nav>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="label-mono text-brand-orange">{project.industry[locale]}</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span className="label-mono text-white/55">{isDE ? "Case Study" : "Case study"}</span>
          </div>
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-[56px] tracking-tight text-white leading-[1.05] max-w-4xl">
            {project.title[locale]}
          </h1>
          <p className="mt-6 text-white/65 text-lg md:text-xl max-w-3xl leading-relaxed">
            {project.summary[locale]}
          </p>
        </div>
      </section>

      {/* HERO IMAGE */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <button
            type="button"
            onClick={() => openLightbox(0)}
            data-testid="hero-image"
            className="group relative w-full rounded-3xl overflow-hidden border border-brand-orange/20 shadow-[0_40px_120px_rgba(255,106,0,0.18)] block"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(255,106,0,0.15),transparent_55%)] z-10 pointer-events-none" />
            <img
              src={project.assets.hero}
              alt={project.title[locale]}
              loading="eager"
              className="w-full h-auto aspect-[16/9] object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/55 backdrop-blur border border-white/15 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="w-4 h-4" />
            </div>
          </button>
        </div>
      </section>

      {/* METRICS — illustrative KPI proof bar */}
      {project.metrics && project.metrics.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="metrics">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-12 h-px bg-brand-orange" />
              <span className="label-mono">{isDE ? "Ergebnisse im Überblick" : "Results at a glance"}</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {project.metrics.map((m, i) => (
                <div
                  key={m.label.en}
                  data-testid={`metric-${i}`}
                  className="glass-strong rounded-2xl p-5 md:p-6 relative overflow-hidden hover:border-brand-orange/40 transition-all"
                >
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-brand-orange/10 blur-3xl rounded-full" />
                  <div className="label-mono text-white/55 mb-2">{m.label[locale]}</div>
                  <div className="font-display font-bold text-2xl md:text-3xl text-brand-orange tracking-tight">
                    {m.value}
                  </div>
                  <div className="mt-1.5 text-[11px] text-white/55 leading-snug">{m.note[locale]}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.22em] text-white/35">
              {isDE
                ? "Illustrative Case-Study-Kennzahlen · echte Daten auf NDA-Anfrage"
                : "Illustrative case-study figures · real data on NDA request"}
            </p>
          </div>
        </section>
      )}

      {/* CHALLENGE / SOLUTION */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          <article className="glass rounded-3xl p-8 md:p-10">
            <div className="label-mono text-white/55 mb-4">{isDE ? "Herausforderung" : "Challenge"}</div>
            <p className="text-white/85 text-base md:text-lg leading-relaxed">{project.challenge[locale]}</p>
          </article>
          <article className="glass rounded-3xl p-8 md:p-10 border-brand-orange/30">
            <div className="label-mono text-brand-orange mb-4">{isDE ? "Lösung" : "Solution"}</div>
            <p className="text-white/85 text-base md:text-lg leading-relaxed">{project.solution[locale]}</p>
          </article>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="before-after-section">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{isDE ? "Vorher / Nachher" : "Before / After"}</span>
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white tracking-tight mb-2">
            {isDE ? "Die Transformation in einem Bild." : "The transformation, in one frame."}
          </h2>
          <p className="text-white/60 max-w-2xl text-base md:text-lg leading-relaxed mb-8">
            {isDE
              ? "Zieh den Schieberegler, um den direkten Vergleich zu sehen."
              : "Drag the handle to reveal the side-by-side comparison."}
          </p>
          <BeforeAfter
            beforeSrc={project.assets.before}
            afterSrc={project.assets.after}
            beforeLabel={isDE ? "VORHER" : "BEFORE"}
            afterLabel={isDE ? "NACHHER" : "AFTER"}
            beforeAlt={`${project.title[locale]} — before`}
            afterAlt={`${project.title[locale]} — after`}
          />
        </div>
      </section>

      <ProjectGallery project={project} isDE={isDE} openLightbox={openLightbox} />

      {/* IMPLEMENTATION */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{isDE ? "Umsetzung" : "Implementation"}</span>
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white tracking-tight mb-8">
            {isDE ? "Wie wir es gebaut haben." : "How we built it."}
          </h2>
          <ul className="space-y-3 max-w-3xl">
            {project.implementation[locale].map((it) => (
              <li key={it} className="flex items-start gap-3 glass rounded-2xl px-5 py-4">
                <Check className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" />
                <span className="text-white/85 leading-relaxed">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TECH STACK */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{isDE ? "Technologie-Stack" : "Technology stack"}</span>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {project.stack.map((s) => (
              <span key={s} className="px-4 py-2 rounded-full glass text-sm text-white/85 hover:border-brand-orange/40 transition-colors">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOME RESULTS */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{isDE ? "Outcomes" : "Outcomes"}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {project.results[locale].map((r, i) => (
              <div key={r} className="glass rounded-2xl p-6 hover:border-brand-orange/40 transition-all">
                <div className="label-mono text-brand-orange mb-3">{String(i + 1).padStart(2, "0")}</div>
                <div className="text-sm text-white/85 leading-relaxed">{r}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{isDE ? "Verwandte Leistungen" : "Related services"}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {project.relatedServices.map((s) => {
              const sc = SERVICE_CONTENT[s];
              if (!sc) return null;
              return (
                <Link
                  key={s}
                  to={`/services/${s}`}
                  data-testid={`project-related-service-${s}`}
                  className="glass rounded-2xl p-6 hover:border-brand-orange/40 transition-all group flex items-center justify-between"
                >
                  <div>
                    <div className="label-mono text-brand-orange mb-1">{sc.hero[locale].kicker}</div>
                    <div className="font-display font-semibold text-white">{sc.hero[locale].h1.split(/[.,—]/)[0]}</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/50 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <ProjectCta isDE={isDE} />

      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <Link to="/portfolio" className="inline-flex items-center gap-2 label-mono text-white/55 hover:text-brand-orange transition-colors">
            <ArrowLeft className="w-4 h-4" /> {isDE ? "Alle Projekte" : "All projects"}
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={galleryImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prev}
        onNext={next}
      />
    </main>
  );
}
